import React, { useState, useEffect, useMemo } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { UserService, User } from '../../services/user';
import { createUserIdToNameMap, resolveTeamName } from '../../utils/userNameResolver';

interface TeamCollaborationNetworkChartProps {
  data: Array<{
    team_name: string | number;
    collaboration_score: number;
    network_centrality: number;
    knowledge_hub_score: number;
    cross_team_connections: number;
    influence_score: number;
    team_size: number;
  }>;
  loading?: boolean;
}

const TeamCollaborationNetworkChart: React.FC<TeamCollaborationNetworkChartProps> = ({ data, loading = false }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // Load users on mount
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await UserService.getAllDirectoryUsers();
        setUsers(users);
      } catch (error) {
        console.error('Failed to load users:', error);
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Create a map of user ID to user name
  const userIdToNameMap = useMemo(() => createUserIdToNameMap(users), [users]);

  // Resolve team names (convert IDs to names)
  const resolvedData = useMemo(() => {
    return data.map(item => ({
      ...item,
      team_name: resolveTeamName(item.team_name, userIdToNameMap)
    }));
  }, [data, userIdToNameMap]);
  if (loading || usersLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Collaboration Network</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Collaboration Network</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No collaboration network data available
        </div>
      </div>
    );
  }

  const getNetworkRole = (centrality: number, influence: number) => {
    if (centrality >= 80 && influence >= 80) return { role: 'Hub', color: '#ef4444' };
    if (centrality >= 60 && influence >= 60) return { role: 'Connector', color: '#f59e0b' };
    if (centrality >= 40 || influence >= 40) return { role: 'Active', color: '#3b82f6' };
    return { role: 'Peripheral', color: '#10b981' };
  };

  const getTeamColor = (index: number) => {
    const colors = [
      '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', 
      '#06b6d4', '#84cc16', '#f97316', '#ec4899', '#6366f1'
    ];
    return colors[index % colors.length];
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Team Collaboration Network</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ScatterChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              type="number" 
              dataKey="network_centrality" 
              name="Network Centrality"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <YAxis 
              type="number" 
              dataKey="influence_score" 
              name="Influence Score"
              tick={{ fontSize: 12 }}
              domain={[0, 100]}
            />
            <Tooltip 
              formatter={(value: number, name: string, props: any) => {
                if (name === 'network_centrality') return [`${value.toFixed(1)}%`, 'Network Centrality'];
                if (name === 'influence_score') return [`${value.toFixed(1)}%`, 'Influence Score'];
                if (name === 'collaboration_score') return [`${value.toFixed(1)}%`, 'Collaboration Score'];
                if (name === 'knowledge_hub_score') return [`${value.toFixed(1)}%`, 'Knowledge Hub Score'];
                if (name === 'cross_team_connections') return [`${value}`, 'Cross-team Connections'];
                if (name === 'team_size') return [`${value}`, 'Team Size'];
                return [value, name];
              }}
              labelFormatter={(label, payload) => {
                if (payload && payload[0]) {
                  return `Team: ${payload[0].payload.team_name}`;
                }
                return '';
              }}
            />
            <Scatter dataKey="influence_score" fill="#8884d8">
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={getTeamColor(index)} 
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Network Roles</h4>
          <div className="space-y-2">
            {[...resolvedData]
              .sort((a, b) => (b.network_centrality + b.influence_score) - (a.network_centrality + a.influence_score))
              .map((item, index) => {
                const role = getNetworkRole(item.network_centrality, item.influence_score);
                return (
                  <div key={`${item.team_name}-${index}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded mr-2" 
                        style={{ backgroundColor: getTeamColor(index) }}
                      ></div>
                      <span className="text-sm font-medium">{item.team_name}</span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">{item.cross_team_connections}</span> connections
                      <span className={`ml-2 text-xs px-2 py-1 rounded`} style={{ 
                        backgroundColor: role.color + '20', 
                        color: role.color 
                      }}>
                        {role.role}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Network Insights</h4>
          <div className="space-y-3">
            <div className="bg-red-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-600">Network Hub</span>
                <span className="font-bold text-red-600">
                  {resolvedData.reduce((max, item) => 
                    (item.network_centrality + item.influence_score) > (max.network_centrality + max.influence_score) ? item : max, 
                    resolvedData[0]
                  )?.team_name}
                </span>
              </div>
              <div className="text-xs text-red-500 mt-1">
                Highest centrality & influence
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Most Connected</span>
                <span className="font-bold text-blue-600">
                  {resolvedData.reduce((max, item) => item.cross_team_connections > max.cross_team_connections ? item : max, resolvedData[0])?.team_name}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...resolvedData.map(d => d.cross_team_connections))} connections
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Knowledge Hub</span>
                <span className="font-bold text-green-600">
                  {resolvedData.reduce((max, item) => item.knowledge_hub_score > max.knowledge_hub_score ? item : max, resolvedData[0])?.team_name}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...resolvedData.map(d => d.knowledge_hub_score)).toFixed(1)}% hub score
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamCollaborationNetworkChart;
